import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/apiClient';
import { useAuth } from './AuthProvider';
import type { SpotKind, SpotType } from '../lib/types';

const STORAGE_KEY = 'wildswim.homeSpot.v1';

/**
 * Plan §20.6 — saved-spot store with hybrid local+backend sync.
 *
 *   - localStorage is the offline-first cache + the unsigned-in
 *     fallback. App is fully usable without an account.
 *   - When signed in, /v1/me/saved-spots becomes the source of
 *     truth: fetched on mount, mutated on save/remove, mirrored
 *     into localStorage so a sign-out doesn't wipe the user's
 *     spots from the local view.
 *   - `homeSpot` semantic mirrors the backend's `isHome` flag.
 */
export interface HomeSpot {
    id: string;
    name: string;
    lat: number;
    lon: number;
    spotType: SpotType;
    spotKind: SpotKind;
}

interface HomeSpotContextValue {
    homeSpot: HomeSpot | null;
    setHomeSpot: (spot: HomeSpot | null) => void;
    savedSpots: HomeSpot[];
    addSavedSpot: (spot: HomeSpot) => void;
    removeSavedSpot: (id: string) => void;
    syncing: boolean;
}

interface PersistShape {
    homeSpot: HomeSpot | null;
    savedSpots: HomeSpot[];
}

interface SavedResponse {
    saved: { isHome: boolean; spot: { id: string; name: string; lat: number; lon: number; spotType: SpotType } }[];
}

const HomeSpotContext = createContext<HomeSpotContextValue>({
    homeSpot: null,
    setHomeSpot: () => undefined,
    savedSpots: [],
    addSavedSpot: () => undefined,
    removeSavedSpot: () => undefined,
    syncing: false,
});

function load(): PersistShape {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return { homeSpot: null, savedSpots: [] };
        const parsed = JSON.parse(raw) as PersistShape;
        return {
            homeSpot: parsed.homeSpot ?? null,
            savedSpots: Array.isArray(parsed.savedSpots) ? parsed.savedSpots : [],
        };
    } catch {
        return { homeSpot: null, savedSpots: [] };
    }
}

function persist(state: PersistShape) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch { /* quota — ignore */ }
}

export function HomeSpotProvider({ children }: { children: ReactNode }) {
    const { isSignedIn } = useAuth();
    const qc = useQueryClient();
    const [state, setState] = useState<PersistShape>(() => load());

    // Backend pull — only when signed in. Becomes source of truth.
    const remote = useQuery({
        queryKey: ['me', 'saved-spots'],
        queryFn: () => api.get<SavedResponse>('/v1/me/saved-spots'),
        enabled: isSignedIn,
        staleTime: 60_000,
    });

    useEffect(() => {
        if (!remote.data) return;
        const savedSpots: HomeSpot[] = remote.data.saved.map((r) => ({
            id: r.spot.id,
            name: r.spot.name,
            lat: r.spot.lat,
            lon: r.spot.lon,
            spotType: r.spot.spotType,
            spotKind: 'swim_spot',
        }));
        const homeRow = remote.data.saved.find((r) => r.isHome);
        const homeSpot: HomeSpot | null = homeRow
            ? {
                  id: homeRow.spot.id,
                  name: homeRow.spot.name,
                  lat: homeRow.spot.lat,
                  lon: homeRow.spot.lon,
                  spotType: homeRow.spot.spotType,
                  spotKind: 'swim_spot',
              }
            : null;
        setState({ homeSpot, savedSpots });
    }, [remote.data]);

    useEffect(() => {
        persist(state);
    }, [state]);

    const setHomeSpot = useCallback(async (spot: HomeSpot | null) => {
        setState((s) => ({ ...s, homeSpot: spot }));
        // `local:` ad-hoc IDs aren't backend rows — keep local-only.
        if (isSignedIn && spot && !spot.id.startsWith('local:')) {
            try {
                await api.post('/v1/me/saved-spots', { spotId: spot.id, isHome: true });
                qc.invalidateQueries({ queryKey: ['me', 'saved-spots'] });
            } catch (err) {
                console.warn('[home-spot] backend setHome failed', err);
            }
        }
    }, [isSignedIn, qc]);

    const addSavedSpot = useCallback(async (spot: HomeSpot) => {
        setState((s) => {
            if (s.savedSpots.some((x) => x.id === spot.id)) return s;
            return {
                ...s,
                savedSpots: [...s.savedSpots, spot],
                homeSpot: s.homeSpot ?? spot,
            };
        });
        if (isSignedIn && !spot.id.startsWith('local:')) {
            try {
                await api.post('/v1/me/saved-spots', { spotId: spot.id });
                qc.invalidateQueries({ queryKey: ['me', 'saved-spots'] });
            } catch (err) {
                console.warn('[home-spot] backend save failed', err);
            }
        }
    }, [isSignedIn, qc]);

    const removeSavedSpot = useCallback(async (id: string) => {
        setState((s) => ({
            ...s,
            savedSpots: s.savedSpots.filter((x) => x.id !== id),
            homeSpot: s.homeSpot?.id === id ? null : s.homeSpot,
        }));
        if (isSignedIn && !id.startsWith('local:')) {
            try {
                await api.delete(`/v1/me/saved-spots/${encodeURIComponent(id)}`);
                qc.invalidateQueries({ queryKey: ['me', 'saved-spots'] });
            } catch (err) {
                console.warn('[home-spot] backend remove failed', err);
            }
        }
    }, [isSignedIn, qc]);

    return (
        <HomeSpotContext.Provider
            value={{
                ...state,
                setHomeSpot: (s) => { void setHomeSpot(s); },
                addSavedSpot: (s) => { void addSavedSpot(s); },
                removeSavedSpot: (id) => { void removeSavedSpot(id); },
                syncing: isSignedIn && remote.isLoading,
            }}
        >
            {children}
        </HomeSpotContext.Provider>
    );
}

export function useHomeSpot() {
    return useContext(HomeSpotContext);
}
